// Central config for every crop-engine tool page.
// mode: 'marketplace' | 'merge' | 'a4' | 'invoice'
// market: forced marketplace for the engine (marketplace mode) or null (auto-detect)

export const CROP_TOOLS = {
  "amazon-shipping-label-crop": {
    slug: "amazon-shipping-label-crop",
    path: "/tools/amazon-shipping-label-crop",
    mode: "marketplace",
    market: "amazon",
    icon: "Package",
    badge: "amazon",
    name: "Amazon Shipping Label Crop",
    promise: "Keep the Amazon label page, drop the invoice, and stamp a clear SKU | Qty line for faster packing.",
    metaTitle: "Amazon Shipping Label Cropper - Remove Invoice Free | Ecroply AI",
    metaDesc: "Crop Amazon shipping labels from your label+invoice PDF in seconds. Keeps the SKU/Qty line, drops the invoice. Free, browser-based, no login.",
    why: [
      "Amazon Seller Central and Easy Ship label PDFs pair each shipping label with a full tax-invoice page. When you print the whole file you waste paper and slow down packing while hunting for the right label.",
      "The Amazon label itself is a flat image with no readable text, so this tool keeps that label page exactly as printed and reads the SKU and quantity from the invoice page to stamp a bold picking line underneath.",
    ],
    features: [
      "Label-only output: the invoice page is removed so you print just what the courier scans.",
      "SKU | Qty line added below the label, read straight from the paired invoice.",
      "Thermal roll sizes for 4×6, 100×150 mm, 3×5 and 100×100 mm printers.",
      "4-up A4 layout with dashed cut guides to save paper on laser printers.",
      "Order numbering with your own start value and prefix, printed in packing order.",
      "Picking summary PDF listing units per SKU and parcels per courier.",
    ],
    steps: [
      "Upload one or more Amazon label PDFs (label page followed by its invoice).",
      "Pick your label size — Default A4, a thermal roll, or a custom size in mm.",
      "Turn on numbering, a summary or a dispatch note if you want them.",
      "Press Start Amazon Crop — everything runs on your device.",
      "Your cleaned label PDF downloads automatically, ready to print at 100%.",
    ],
    benefits: [
      "Less paper: one label per parcel instead of a two-page invoice bundle.",
      "Faster packing tables with a visible SKU and quantity on every label.",
      "Consistent output whether you print on thermal rolls or A4 sheets.",
      "No uploads, no waiting — the PDF is built in your browser.",
    ],
    whoFor: ["Amazon Easy Ship sellers", "Warehouse packing teams", "Multi-marketplace sellers", "Small fulfillment centers"],
    comparison: "A generic PDF cropper trims a fixed rectangle from every page. This tool understands the Amazon label-plus-invoice structure, keeps the correct label page, and adds a picking line from the invoice — something a blind crop can never do.",
    tips: [
      "Always print at 100% / actual size so barcodes stay scannable.",
      "Double-check the address block is fully inside the label before sticking it on.",
      "Group a batch by courier so your pickup handover is quicker.",
      "Keep the invoice PDF separately if your buyer needs a GST copy.",
    ],
    faq: [
      { q: "Does this crop Amazon labels without uploading my files?", a: "Yes. Your Amazon PDF is processed in your browser on this device and is not uploaded to a server." },
      { q: "Why can't you read text from the Amazon label itself?", a: "Amazon renders the shipping label as an image, so there is no selectable text on it. We read the SKU and quantity from the paired invoice page instead." },
      { q: "What is the SKU | Qty line?", a: "It is a bold line printed under the label, for example IA-QS34-FOMP | Qty - 2, taken from the invoice so packers can pick the right item quickly." },
      { q: "Can I print Amazon labels on a thermal printer?", a: "Yes, choose a thermal size such as 4×6 inch or 100×150 mm and print at actual size. We have not tested on a physical thermal printer, so verify the first label." },
      { q: "My Amazon label looks different — will the SKU line be in the right place?", a: "The position is measured from one Seller Central layout. A very different layout may need new coordinates, so check the first output." },
      { q: "Can I combine Amazon labels with Flipkart and Meesho?", a: "Yes, use the Merge Marketplace Labels tool to mix files into one sorted print run." },
      { q: "What does grouping by courier do on Amazon pages?", a: "Amazon labels are images, so courier reads as Amazon Transport and payment as Unknown. Grouping still sorts your run consistently." },
      { q: "Is there any limit on how many labels I can crop?", a: "No fixed limit. Large PDFs simply take a little longer because everything runs on your device." },
    ],
    related: [
      { label: "Merge Marketplace Labels", path: "/tools/merge-marketplace-labels" },
      { label: "A4 Label Maker", path: "/tools/a4-label-maker" },
      { label: "Guide: How to Crop Amazon Shipping Labels", path: "/blog/how-to-crop-amazon-shipping-labels-from-pdf" },
    ],
  },

  "flipkart-shipping-label-crop": {
    slug: "flipkart-shipping-label-crop",
    path: "/tools/flipkart-shipping-label-crop",
    mode: "marketplace",
    market: "flipkart",
    icon: "Truck",
    badge: "flipkart",
    name: "Flipkart Shipping Label Crop",
    promise: "Turn full-page Flipkart label-plus-invoice sheets into clean, full-width labels only.",
    metaTitle: "Flipkart Shipping Label Cropper - Label Only, Free | Ecroply AI",
    metaDesc: "Turn Flipkart's full-page label+invoice PDF into a clean, full-width shipping label. Free, browser-based cropper – no login required.",
    why: [
      "A Flipkart label PDF puts one order on each A4 page, with the shipping label on top and the tax invoice below. Printing the sheet as-is wastes half the page and buries the label.",
      "This tool keeps only the label region, scales it to a clean 900×1500 point page and centres it, so every printout is label-only and ready for the courier.",
    ],
    features: [
      "Label-only output at a tidy 900×1500 points, scaled to full width.",
      "Courier and payment (COD / Prepaid) read from the label text for grouping.",
      "Thermal roll presets plus a custom size in millimetres.",
      "4-up A4 layout with dashed cut lines for laser printing.",
      "Sort by SKU and group by courier or payment across a batch.",
      "Order numbering and an optional picking summary PDF.",
    ],
    steps: [
      "Upload one or more Flipkart label PDFs (one order per page).",
      "Choose Default, a thermal size, or a custom label size.",
      "Optionally group by courier, sort by SKU or add order numbers.",
      "Press Start Flipkart Crop to process on your device.",
      "The label-only PDF downloads automatically for printing at 100%.",
    ],
    benefits: [
      "Clean, consistent labels with no invoice clutter.",
      "Half the paper compared with printing the full sheet.",
      "Batches sorted by courier make pickup handovers faster.",
      "Runs fully in the browser — nothing leaves your device.",
    ],
    whoFor: ["Flipkart sellers", "Warehouse managers", "Multi-marketplace sellers", "Fulfillment centers"],
    comparison: "Unlike a generic cropper that cuts the same rectangle everywhere, this tool reads the Flipkart layout, keeps the label region, and can group your run by the courier printed on each label.",
    tips: [
      "Print at 100% so the courier barcode scans first time.",
      "Confirm the pincode and address are inside the label edges.",
      "Group by courier before a bulk pickup to speed up handover.",
      "Use 4-up on A4 if you do not have a thermal printer.",
    ],
    faq: [
      { q: "Are my Flipkart labels uploaded anywhere?", a: "No. The PDF is processed in your browser on this device and is not uploaded to a server." },
      { q: "What size is the cropped Flipkart label?", a: "Each label is output at 900×1500 points, scaled to full width and vertically centred, label only." },
      { q: "My Flipkart PDF has two labels on one page — will it work?", a: "PDFs that place two labels on a single page are rejected with a clear message, because the crop expects one order per page." },
      { q: "Does it read the courier from the label?", a: "Yes, when the courier name is real text on the label (Xpress Bees, Shadowfax, Delhivery, E-Kart and so on) it is used for grouping." },
      { q: "Can I print on a thermal printer?", a: "Yes, pick a thermal size and print at actual size. Physical thermal printing has not been tested, so check the first label." },
      { q: "Does the colour/size sort work for Flipkart?", a: "No. Only Meesho labels carry Size and Color columns, so that sort applies to Meesho and is hidden here." },
      { q: "Can I mix Flipkart with other marketplaces?", a: "Yes, use Merge Marketplace Labels to combine Amazon, Flipkart and Meesho into one run." },
      { q: "Is there a limit on the number of orders?", a: "No fixed limit; larger files just take a little longer to process locally." },
    ],
    related: [
      { label: "Shopsy Label Crop", path: "/tools/shopsy-shipping-label-crop" },
      { label: "Invoice Cropper", path: "/tools/invoice-cropper" },
      { label: "Guide: How to Crop Flipkart Shipping Labels", path: "/blog/how-to-crop-flipkart-shipping-labels" },
    ],
  },

  "meesho-shipping-label-crop": {
    slug: "meesho-shipping-label-crop",
    path: "/tools/meesho-shipping-label-crop",
    mode: "marketplace",
    market: "meesho",
    icon: "ShoppingBag",
    badge: "meesho",
    name: "Meesho Shipping Label Crop",
    promise: "Crop Meesho labels from Customer Address to Product Details and drop the tax-invoice strip.",
    metaTitle: "Meesho Shipping Label Cropper - Remove Tax Invoice | Ecroply AI",
    metaDesc: "Crop Meesho shipping labels from Customer Address to Product Details and drop the tax-invoice strip. Free, browser-based, no login needed.",
    why: [
      "A Meesho label PDF stacks the shipping label, a TAX INVOICE strip and the full invoice on one page per order. Only the top block — Customer Address down to Product Details — needs to go on the parcel.",
      "This tool keeps that label block at a clean 900-point width, drops the tax-invoice strip and the invoice, and can even sort your batch by the colour and size printed on Meesho labels.",
    ],
    features: [
      "Label-only crop from Customer Address to the Product Details row.",
      "Colour and size sorting — a Meesho-only feature read from the label columns.",
      "Courier and payment detection for grouping (Xpress Bees, Shadowfax, Delhivery, E-Kart).",
      "Thermal roll sizes and custom millimetre sizes.",
      "4-up A4 layout with dashed cut guides.",
      "Order numbering and a picking summary with units per SKU.",
    ],
    steps: [
      "Upload one or more Meesho label PDFs (one order per page).",
      "Choose Default, a thermal size or a custom size.",
      "Optionally sort by colour and size, group by courier, or number orders.",
      "Press Start Meesho Crop to process locally.",
      "Your label-only PDF downloads automatically for 100% printing.",
    ],
    benefits: [
      "Only the address-to-product block prints — no tax-invoice strip.",
      "Colour/size sorting groups similar items for faster picking.",
      "Courier grouping speeds up handover at pickup.",
      "Everything runs on your device with no uploads.",
    ],
    whoFor: ["Meesho sellers", "Reseller and dropship sellers", "Warehouse pickers", "Fulfillment teams"],
    comparison: "A generic cropper cannot tell the label from the invoice strip. This tool understands the Meesho layout, keeps the correct block, and uniquely sorts by the Size and Color columns Meesho prints.",
    tips: [
      "Sort by colour and size to pick faster from mixed-variant stock.",
      "Print at 100% so the barcode and address stay crisp.",
      "Group by courier before a bulk manifest for quicker pickup.",
      "Keep the tax invoice separately if a buyer asks for it.",
    ],
    faq: [
      { q: "Do you upload my Meesho labels?", a: "No. Your PDF is processed in your browser on this device and is not uploaded to a server." },
      { q: "What part of the Meesho page is kept?", a: "The label block from Customer Address down to the Product Details row. The TAX INVOICE strip and the invoice are dropped." },
      { q: "How wide is the cropped Meesho label?", a: "It is output at 900 points wide, with the height scaled to match the cropped block." },
      { q: "Can I sort by colour and size?", a: "Yes. Meesho labels carry Size and Color columns, so this tool can sort colour-first or size-first, with sizes ordered S, M, L, XL." },
      { q: "Does colour/size sorting work on other marketplaces?", a: "No. Only Meesho labels have those columns, so on Flipkart and Amazon that control is hidden." },
      { q: "Which couriers can it detect?", a: "Common ones printed as text, such as Xpress Bees, Shadowfax, Delhivery and E-Kart Logistics." },
      { q: "Can I print on a thermal printer?", a: "Yes, pick a thermal size and print at actual size. Thermal printing has not been physically tested — verify the first label." },
      { q: "Is there a limit on orders per file?", a: "No fixed limit; bigger files just take a little longer to process on your device." },
    ],
    related: [
      { label: "Invoice Cropper", path: "/tools/invoice-cropper" },
      { label: "A4 Label Maker", path: "/tools/a4-label-maker" },
      { label: "Guide: How to Crop Meesho Shipping Labels", path: "/blog/how-to-crop-meesho-shipping-labels" },
    ],
  },

  "shopsy-shipping-label-crop": {
    slug: "shopsy-shipping-label-crop",
    path: "/tools/shopsy-shipping-label-crop",
    mode: "marketplace",
    market: "flipkart",
    icon: "Store",
    badge: "shopsy",
    name: "Shopsy Shipping Label Crop",
    promise: "Shopsy uses the Flipkart label format, so this tool crops it to a clean label-only page.",
    metaTitle: "Shopsy Shipping Label Crop — Free Label Cropper | Ecroply AI",
    metaDesc: "Crop Shopsy shipping labels in your browser. Shopsy uses the Flipkart label layout, so output is a clean 900×1500 label with the invoice removed. Free, no login.",
    why: [
      "Shopsy runs on Flipkart's logistics, so a Shopsy label PDF uses the same one-order-per-page layout with the label on top and the invoice below.",
      "This tool applies the Flipkart crop: it keeps only the label region, scales it to a clean 900×1500 point page and removes the invoice, so you print label-only.",
    ],
    features: [
      "Label-only output at 900×1500 points, matching the Flipkart format.",
      "Courier and payment detection for grouping across a batch.",
      "Thermal roll presets and custom millimetre sizing.",
      "4-up A4 layout with dashed cut guides.",
      "Sort by SKU and group by courier or payment.",
      "Order numbering and an optional picking summary.",
    ],
    steps: [
      "Upload one or more Shopsy label PDFs (one order per page).",
      "Pick Default, a thermal size or a custom size.",
      "Optionally group, sort by SKU or number your orders.",
      "Press Start Shopsy Crop to process on your device.",
      "The label-only PDF downloads automatically for 100% printing.",
    ],
    benefits: [
      "Clean label-only pages with no invoice below.",
      "Consistent 900×1500 output ready for thermal or A4.",
      "Courier grouping speeds up bulk pickups.",
      "Fully in-browser — nothing is uploaded.",
    ],
    whoFor: ["Shopsy sellers", "Flipkart + Shopsy sellers", "Warehouse pickers", "Fulfillment centers"],
    comparison: "Rather than a blind rectangle crop, this tool applies the tested Flipkart layout logic that Shopsy shares, so the label region is kept accurately and the run can be grouped by courier.",
    tips: [
      "Print at 100% for scannable barcodes.",
      "Verify the address block sits inside the label edges.",
      "Group by courier for faster pickup handover.",
      "Use 4-up on A4 when you do not have a thermal printer.",
    ],
    faq: [
      { q: "Why does Shopsy use the Flipkart cropper?", a: "Shopsy is a Flipkart product and its shipping labels use the Flipkart layout, so the same crop logic applies." },
      { q: "Are my Shopsy files uploaded?", a: "No. The PDF is processed in your browser on this device and is not uploaded to a server." },
      { q: "What size is the output label?", a: "900×1500 points, label only, scaled to full width and centred." },
      { q: "My Shopsy PDF has two labels on a page — will it work?", a: "PDFs with two labels on one page are rejected with a clear message; the crop expects one order per page." },
      { q: "Can it detect the courier?", a: "Yes, when the courier is real text on the label it is used for grouping." },
      { q: "Does colour/size sorting apply?", a: "No, that is a Meesho-only feature, so the control is hidden here." },
      { q: "Can I print on thermal rolls?", a: "Yes, choose a thermal size and print at actual size. Thermal printing has not been physically tested." },
      { q: "Any limit on orders?", a: "No fixed limit; larger files just take longer to process locally." },
    ],
    related: [
      { label: "Flipkart Label Crop", path: "/tools/flipkart-shipping-label-crop" },
      { label: "Merge Marketplace Labels", path: "/tools/merge-marketplace-labels" },
      { label: "Guide: How to Crop Shopsy Shipping Labels", path: "/blog/how-to-crop-shopsy-shipping-labels" },
    ],
  },

  "merge-marketplace-labels": {
    slug: "merge-marketplace-labels",
    path: "/tools/merge-marketplace-labels",
    mode: "merge",
    market: null,
    icon: "Layers",
    badge: null,
    name: "Merge Marketplace Labels",
    promise: "Drop in Amazon, Flipkart and Meesho files together and get one sorted print run, grouped by courier.",
    metaTitle: "Merge Marketplace Labels — Amazon + Flipkart + Meesho | Ecroply AI",
    metaDesc: "Combine Amazon, Flipkart and Meesho shipping label PDFs into one sorted print run, grouped by courier and payment. Free, in-browser, no account.",
    why: [
      "Selling on more than one marketplace means juggling separate label files in different layouts. Printing them one by one is slow and easy to get out of order.",
      "This tool auto-detects each file's marketplace, crops every label to a clean page, and merges them into a single PDF grouped by courier and payment so your whole day's dispatch prints in one go.",
    ],
    features: [
      "Auto-detects Amazon, Flipkart and Meesho files — no manual tagging.",
      "One clean label per order, invoices removed, across all marketplaces.",
      "Grouped by courier and payment by default for tidy pickups.",
      "Per-file summary chips showing the detected marketplace and order count.",
      "Order numbering that runs across the whole merged batch.",
      "Optional picking summary with units per SKU and parcels per courier.",
    ],
    steps: [
      "Upload your Amazon, Flipkart and Meesho label PDFs together.",
      "Check the detected-marketplace chip and order count on each file.",
      "Adjust grouping, numbering or a summary if needed.",
      "Press Merge labels to build one sorted PDF on your device.",
      "The merged label PDF downloads automatically, grouped by courier.",
    ],
    benefits: [
      "One print run for the whole day instead of file-by-file.",
      "Courier grouping makes multi-marketplace pickups painless.",
      "Consistent label size regardless of source marketplace.",
      "Runs in the browser — no files are uploaded.",
    ],
    whoFor: ["Multi-marketplace sellers", "3PL and fulfillment centers", "Warehouse dispatch teams", "Growing D2C brands"],
    comparison: "A generic PDF merge just staples files end to end. This tool crops each label, detects its marketplace, and reorders the combined run by courier — a print run, not a pile.",
    tips: [
      "Group by courier so each pickup partner gets a contiguous stack.",
      "Turn on numbering to reconcile against your summary list.",
      "Print at 100% so every barcode scans.",
      "Keep a summary PDF as your picking checklist.",
    ],
    faq: [
      { q: "Which marketplaces can I merge?", a: "Amazon, Flipkart and Meesho label PDFs. Each file is auto-detected." },
      { q: "Are my files uploaded?", a: "No. Everything is processed in your browser on this device and is not uploaded to a server." },
      { q: "How are labels ordered in the merged file?", a: "By courier and payment by default, so parcels for the same courier print together." },
      { q: "What if I drop in a file that isn't a supported label?", a: "That file shows a clear error on its own row and the rest still merge." },
      { q: "Do order numbers run across all marketplaces?", a: "Yes, numbering is applied in final print order across the whole merged batch." },
      { q: "Can I still choose thermal sizes?", a: "Yes, the same label-size options apply to the merged output." },
      { q: "Does colour/size sort work here?", a: "It only affects Meesho orders; non-Meesho orders sort after them." },
      { q: "Is there a file limit?", a: "No fixed limit; larger combined runs simply take a little longer locally." },
    ],
    related: [
      { label: "A4 Label Maker", path: "/tools/a4-label-maker" },
      { label: "Invoice Cropper", path: "/tools/invoice-cropper" },
    ],
  },

  "a4-label-maker": {
    slug: "a4-label-maker",
    path: "/tools/a4-label-maker",
    mode: "a4",
    market: null,
    icon: "LayoutGrid",
    badge: null,
    name: "A4 Label Maker (4-up)",
    promise: "Fit four cropped labels on one A4 sheet with dashed cut guides — perfect for laser printers.",
    metaTitle: "A4 Label Maker — 4 Labels Per Sheet | Ecroply AI",
    metaDesc: "Place four cropped shipping labels on one A4 sheet with dashed cut guides. Works with Amazon, Flipkart and Meesho. Free, in-browser, no login.",
    why: [
      "Not everyone has a thermal printer. On a plain A4 laser or inkjet printer, one label per page wastes three-quarters of every sheet.",
      "This tool auto-detects each label, crops it, and arranges four per A4 sheet in a 2×2 grid with dashed cut guides, so you print, cut and stick with almost no waste.",
    ],
    features: [
      "Four labels per A4 sheet in a clean 2×2 grid.",
      "Dashed cut guides around every cell for easy trimming.",
      "Auto-detects Amazon, Flipkart and Meesho files.",
      "Wide labels rotate automatically to fill each cell.",
      "Group by courier or payment and number your orders.",
      "Optional picking summary appended to the run.",
    ],
    steps: [
      "Upload your label PDFs from any supported marketplace.",
      "Optionally group, sort or number the orders.",
      "Press Make A4 sheets to build the 4-up layout locally.",
      "Cut along the dashed guides after printing at 100%.",
      "The A4 PDF downloads automatically, ready for a laser printer.",
    ],
    benefits: [
      "Up to 75% less paper than one label per sheet.",
      "No thermal printer required — any A4 printer works.",
      "Dashed guides make cutting quick and straight.",
      "Fully in-browser with no uploads.",
    ],
    whoFor: ["Sellers without a thermal printer", "Home-based sellers", "Small warehouses", "Anyone printing on A4"],
    comparison: "A generic imposition tool doesn't understand shipping labels. This one crops each label first, then lays four per sheet with guides — labels, not raw pages.",
    tips: [
      "Print at 100% / actual size so barcodes remain scannable.",
      "Use plain-paper self-adhesive A4 label sheets for best results.",
      "Cut just inside the dashed guides for clean edges.",
      "Group by courier so each cut stack stays together.",
    ],
    faq: [
      { q: "How many labels fit on a page?", a: "Four, in a 2×2 grid, with dashed cut guides around each." },
      { q: "Which marketplaces are supported?", a: "Amazon, Flipkart and Meesho, auto-detected from each file." },
      { q: "Are files uploaded?", a: "No. The layout is built in your browser on this device and is not uploaded to a server." },
      { q: "Do wide labels get cut off?", a: "No, wide labels rotate to fill the cell so nothing is clipped." },
      { q: "Can I pick a thermal size here?", a: "No, this page always produces 4-up A4 sheets, so label size is fixed to the layout." },
      { q: "Can I number the labels?", a: "Yes, numbering prints on each label in final print order and appears in the summary." },
      { q: "Does grouping work?", a: "Yes, you can group by courier or payment before the labels are placed." },
      { q: "Any file limit?", a: "No fixed limit; more labels simply mean more A4 pages." },
    ],
    related: [
      { label: "Merge Marketplace Labels", path: "/tools/merge-marketplace-labels" },
      { label: "Meesho Label Crop", path: "/tools/meesho-shipping-label-crop" },
    ],
  },

  "invoice-cropper": {
    slug: "invoice-cropper",
    path: "/tools/invoice-cropper",
    mode: "invoice",
    market: null,
    icon: "ReceiptText",
    badge: null,
    name: "Invoice Cropper",
    promise: "Pull just the invoices out of your label PDFs — cropped for Flipkart and Meesho, full pages for Amazon.",
    metaTitle: "Invoice Cropper — Invoices Only from Label PDFs | Ecroply AI",
    metaDesc: "Extract only the invoices from Amazon, Flipkart and Meesho label PDFs, cropped and sorted. Add custom text and dates. Free, in-browser, no login.",
    why: [
      "For GST records and buyer copies you often need the invoices, not the labels. Manually deleting label pages one by one is tedious.",
      "This tool does the opposite of the label crop: it keeps only the invoices — cropped for Flipkart and Meesho, full pages for Amazon — in your chosen sort order, with optional custom text and a date in the margin.",
    ],
    features: [
      "Invoices-only output, label pages removed automatically.",
      "Cropped invoices for Flipkart and Meesho, full pages for Amazon.",
      "Auto-detects each file's marketplace.",
      "Sort by SKU and group by courier or payment.",
      "Custom footer text and a print date in the bottom margin.",
      "Skips orders that have no invoice and tells you how many.",
    ],
    steps: [
      "Upload your Amazon, Flipkart or Meesho label PDFs.",
      "Choose sorting or grouping if you want a specific order.",
      "Add custom footer text or a print date if needed.",
      "Press Crop invoices to build the invoices-only PDF locally.",
      "Your invoices PDF downloads automatically.",
    ],
    benefits: [
      "A clean invoice file for accounting and GST records.",
      "No manual page deletion.",
      "Consistent sorting to match your label run.",
      "Runs in the browser — nothing is uploaded.",
    ],
    whoFor: ["Sellers filing GST", "Accounts teams", "Multi-marketplace sellers", "Anyone archiving invoices"],
    comparison: "Instead of cropping labels, this tool inverts the logic to keep only invoices — cropped or full depending on marketplace — which a generic PDF tool cannot decide for you.",
    tips: [
      "Add a print date so archived invoices are easy to file.",
      "Keep footer custom text short — it prints in the bottom margin.",
      "Sort by SKU to reconcile against your stock records.",
      "Store the invoice PDF alongside your label run for each dispatch.",
    ],
    faq: [
      { q: "What does invoices-only output contain?", a: "Only the invoice pages — cropped for Flipkart and Meesho, full pages for Amazon — in your chosen sort order." },
      { q: "Are label pages removed?", a: "Yes, this tool keeps invoices and drops the labels." },
      { q: "Are my files uploaded?", a: "No. Everything is processed in your browser on this device and is not uploaded to a server." },
      { q: "What if an order has no invoice?", a: "It is skipped, and the result tells you how many orders were skipped." },
      { q: "Can I add custom text or a date?", a: "Yes, both print in the bottom margin of each invoice page." },
      { q: "Why can't I pick a thermal size here?", a: "Invoices-only mode locks label-size and layout options, since those apply to labels, not invoices." },
      { q: "Does grouping work?", a: "Yes, you can group by courier or payment and sort by SKU." },
      { q: "Any file limit?", a: "No fixed limit; larger files just take a little longer locally." },
    ],
    related: [
      { label: "Meesho Label Crop", path: "/tools/meesho-shipping-label-crop" },
      { label: "Merge PDF", path: "/tools/merge-pdf" },
    ],
  },
};

export const CROP_TOOL_LIST = Object.values(CROP_TOOLS);
